import { cn } from "cn";
import { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useOpenSnapshot } from "@/hooks/use-open-snapshot";
import { partitionSelected } from "@/libs/partition-selected";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { FilterValueListSkeleton } from "../_components/filter-value-list-skeleton";
import { ValueIconStack } from "../_components/value-icon-stack";
import { useFilters } from "../_hooks/use-filters";
import { useOptionCounts } from "../_hooks/use-option-counts";
import type { FilterField, FilterOption } from "../types";

/** Multi-choice select (checkbox list). The field MUST provide `useOptions`. */
export const MultiSelectFilter = ({ field }: { field: FilterField }) => {
  const { filters, modify, clear } = useFilters();
  const raw = (filters as Record<string, unknown>)[field.key];
  const selectedValues = Array.isArray(raw) ? (raw as string[]) : [];
  const [open, setOpen] = useState(() => selectedValues.length === 0);
  // select fields always declare useOptions
  const { options: listedOptions, isLoading } = field.useOptions!({
    selectedValues,
    open,
  });
  const options = useOptionCounts(field, listedOptions, { enabled: open });

  const toggle = (optionValue: string) => {
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];
    modify({ [field.key]: next });
  };

  // Selected options float to the top (separated), mirroring the entity widget
  // and the legacy command lists — but grouped by the selection the popover
  // OPENED with, so ticking a row checks it in place instead of moving it
  // across the separator and dragging the scroll position with it. Reopening
  // regroups.
  const openedSelected = useOpenSnapshot(selectedValues, { open });
  const openedKeys = useMemo(() => new Set(openedSelected), [openedSelected]);
  const { selected: selectedOptions, unselected: unselectedOptions } =
    partitionSelected(options, (option) => option.value, openedKeys);

  // The chip summarises the CURRENT selection, so it reads live state rather
  // than the frozen grouping.
  const chipSelectedOptions = options.filter((option) =>
    selectedValues.includes(option.value),
  );

  // Show the selected option icon(s) in the chip value slot (status dots, visit
  // status / type icons), mirroring the entity widget — not a bare count.
  const valueLabel =
    selectedValues.length === 0 ? (
      EMPTY_VALUE_LABEL
    ) : (
      <span className="flex gap-1 items-center">
        <ValueIconStack options={chipSelectedOptions} />
        <span>
          {selectedValues.length === 1
            ? (chipSelectedOptions[0]?.label ?? selectedValues[0])
            : `${selectedValues.length} selected`}
        </span>
      </span>
    );

  const renderOption = (option: FilterOption) => (
    <CommandItem
      key={option.value}
      value={option.value}
      keywords={[option.label, ...(option.keywords ?? [])]}
      onSelect={() => toggle(option.value)}
      className="group text-text-dark-high flex gap-2 items-center"
    >
      <Checkbox checked={selectedValues.includes(option.value)} />
      {option.icon}
      {option.detail ?? (
        <span className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all">
          {option.label}
        </span>
      )}
      {option.count !== undefined && (
        <span className="ml-auto shrink-0 text-sm font-normal text-text-dark-low">
          {option.count}
        </span>
      )}
    </CommandItem>
  );

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={valueLabel}
      selectedCount={selectedValues.length}
      open={open}
      onOpenChange={setOpen}
      onRemove={() => clear(field.key)}
    >
      <Command
        className={cn(
          /* sorted */
          "bg-bg-surface-elevate-low",
          field.popoverWidthClassName ?? "w-50",
        )}
      >
        {field.searchable !== false && (
          <CommandInput placeholder={`${field.label}...`} className="h-9" />
        )}
        <CommandList>
          {isLoading && options.length === 0 ? (
            <div className="px-2 py-4">
              <FilterValueListSkeleton />
            </div>
          ) : (
            <>
              <CommandEmpty>No results found.</CommandEmpty>
              {selectedOptions.length > 0 && (
                <CommandGroup>{selectedOptions.map(renderOption)}</CommandGroup>
              )}
              {selectedOptions.length > 0 && unselectedOptions.length > 0 && (
                <CommandSeparator />
              )}
              {unselectedOptions.length > 0 && (
                <CommandGroup>
                  {unselectedOptions.map(renderOption)}
                </CommandGroup>
              )}
            </>
          )}
        </CommandList>
      </Command>
    </ActiveFilterChip>
  );
};
